import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listNotifications, notificationsUnreadCount, markNotificationsRead, deleteNotification, deleteAllNotifications } from '../api/notifications';
import { useToast } from './use-toast';
import { messageChime } from '../lib/sounds';

export const useNotifications = (userId) => {
  const { t } = useTranslation('common');
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState(false);
  const revision = useRef(0);
  const writing = useRef(false);
  const refresh = useCallback(async () => {
    if (!userId || writing.current) return;
    const seq = ++revision.current;
    setLoading(true);
    try {
      const [rows, count] = await Promise.all([listNotifications({ limit: 30 }), notificationsUnreadCount()]);
      if (seq !== revision.current) return;
      setItems(rows); setUnread(count.unread); setError(false);
    } catch (_) {
      if (seq === revision.current) setError(true);
    } finally {
      if (seq === revision.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setItems([]); setUnread(0); setError(false);
    if (!userId) return undefined;
    refresh();
    const onVisible = () => { if (!document.hidden) refresh(); };
    const timer = setInterval(onVisible, 30000);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    const token = localStorage.getItem('ig_token');
    const seen = new Set();
    let stream;
    if (token) {
      try {
        stream = new EventSource(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/stream?token=${encodeURIComponent(token)}`);
      } catch (_) { /* Polling remains available in clients without EventSource. */ }
    }
    if (stream) {
      stream.addEventListener('notification', (event) => {
        try {
          const item = JSON.parse(event.data);
          if (!seen.has(item.id)) {
            seen.add(item.id);
            if (seen.size > 500) seen.delete(seen.values().next().value);
            if (!item.read) messageChime();
          }
          refresh();
        } catch (_) { /* Ignore malformed frames; polling resynchronizes. */ }
      });
      stream.addEventListener('notifications_changed', refresh);
    }
    return () => {
      revision.current += 1;
      clearInterval(timer); stream?.close();
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userId, refresh]);

  const mutate = async (key, action, update) => {
    if (writing.current) return false;
    writing.current = true;
    revision.current += 1; // An older list must not resurrect a deleted item.
    setBusy(key); setLoading(false);
    try {
      const result = await action();
      setItems(update); setUnread(result.unread);
      return true;
    } catch (_) {
      toast({ title: t('notification_center.action_failed'), variant: 'destructive' });
      return false;
    } finally {
      writing.current = false; setBusy(''); refresh();
    }
  };
  const markRead = (id) => mutate(id ? `read-${id}` : 'read-all', () => markNotificationsRead(id ? [id] : null),
    (rows) => rows.map((row) => (!id || row.id === id ? { ...row, read: true } : row)));
  const remove = (id) => mutate(`delete-${id}`, () => deleteNotification(id), (rows) => rows.filter((row) => row.id !== id));
  const removeAll = () => mutate('delete-all', deleteAllNotifications, () => []);
  return { items, unread, loading, busy, error, refresh, markRead, remove, removeAll };
};